import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { wishItemRequestSchema } from "validations/wishItem"
import { CREATE } from "constants/index"
import { PATH_INFOS } from "utils/navigatorUtils"
import { useAppSelector } from "appRedux/hooks"
import { updateCurrentPageInfo, updateSearchedUserIsLoggedInUser } from "appRedux/slices/globalSlice"
import { updateCurrentItemIndex } from "appRedux/slices/wishListSlice"
import { useCreateWishItemMutation } from "appRedux/slices/wishItemApiSlice"
import { useAuth } from "context/AuthContext"
import WishItemForm from "components/templates/WishItemForm"

export default function CreateNewItemPage() {
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const previousPageInfo = useAppSelector((state) => state.global.previousPageInfo)
  const itemFormMode = useAppSelector((state) => state.itemForm.mode)
  const wishListState = useAppSelector((state) => state.wishList)

  const [createWishItem, createWishItemResult] = useCreateWishItemMutation()

  const { user } = useAuth()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    dispatch(updateSearchedUserIsLoggedInUser(true))
  }, [])

  useEffect(() => {
    dispatch(updateCurrentPageInfo({
      ...PATH_INFOS.item,
      title: itemFormMode === CREATE ? t(PATH_INFOS.item.title) : t("items.editItem")
    }))
  }, [currentPageInfo?.key])

  useEffect(() => {
    if (createWishItemResult.status !== "fulfilled") return
    if (!createWishItemResult?.data?.id) return
    handleWishItemCreatedSuccessfully(createWishItemResult?.data?.id)
  }, [createWishItemResult.status])

  function handleWishItemCreatedSuccessfully(id: number) {
    const handle = user?.handle
    if (!handle) return
    const listId = wishListState?.listInfo?.id
    if (listId) {
      navigateToRelatedList(handle, listId)
    } else {
      dispatch(updateCurrentItemIndex(0))
      navigate(`/user/${handle}/item/${id}`, { replace: true })
    }
  }

  function navigateToRelatedList(handle: string, listId: number) {
    if (previousPageInfo.path === PATH_INFOS.viewList.path) {
      navigate(-1)
      return
    }
    navigate(`/user/${handle}/list/${listId}`, { replace: true })
  }

  function isCTADisabled(dirtyFields: object) {
    return !dirtyFields.hasOwnProperty("name") || createWishItemResult.isLoading
  }

  async function handleAddWishItem(value: WishItemRequestPayload) {
    const formData = new FormData()
    try {
      const parsedValue = await wishItemRequestSchema.parseAsync(value)
      Object.entries(parsedValue).forEach(([key, val]) => {
        formData.append(key, val)
      })
      createWishItem(formData)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <WishItemForm
      isCTADisabled={isCTADisabled}
      isCTALoading={() => createWishItemResult.isLoading}
      onSubmit={handleAddWishItem}
    />
  )
}
